import { arcLiveSections, bullseyeLiveSections, rankLiveRows } from "./liveMatch";
import { matchPresentationPhase } from "./matchPresentation";

export const isCompletedDetail = (match, now) => Boolean(match && matchPresentationPhase(match, now) === "completed");
export const resultKey = row => String(row?.sk || row?.entryId || row?.competitorId || row?.competitorExternalId || "");
const text = value => String(value ?? "").trim();

// These identifiers come from the authenticated my-registration response. Names,
// email addresses, and membership numbers are never used to claim a public result.
export function isOwnResult(row, registration) {
  if (!row || !registration) return false;
  const raw = row.raw || row;
  if (raw.matchId && registration.matchId && raw.matchId !== registration.matchId) return false;
  const entries = [registration.entryId, registration.primaryEntryId, ...(registration.entryIds || []), ...(registration.entries || []).map(entry => entry.entryId)].map(text).filter(Boolean);
  if ([raw.entryId, raw.canonicalEntryId].some(id => id && entries.includes(text(id)))) return true;
  if (registration.participantId && text(raw.participantId || raw.competitorId) === text(registration.participantId)) return true;
  if (registration.personId && text(raw.personId) === text(registration.personId)) return true;
  return Boolean(registration.competitorId && raw.competitorId && text(registration.competitorId) === text(raw.competitorId));
}

export function completedArcSections(match, matchRows, stageRows, stages, rankedMatchRows) {
  const rankedKeys = new Set(rankedMatchRows.map(resultKey));
  return arcLiveSections(match, matchRows, stageRows, stages).map((section, index) => ({
    ...section,
    label: index === 0 ? "Match total" : section.label,
    rows: section.rows.map(row => ({
      ...row,
      cardId: resultKey(row.raw),
      // Retain the established match ranking eligibility; never turn a partial
      // upload into a completed result simply because the event date has passed.
      rankEligible: (index === 0 ? rankedKeys.has(resultKey(row.raw)) : row.rankEligible) && row.score !== null && row.score >= 0,
      progressLabel: row.state === "excluded" ? row.progressLabel
        : index === 0 ? rankedKeys.has(resultKey(row.raw)) ? "Complete score" : "Incomplete score"
          : row.progressLabel,
    })),
  }));
}

export function completedBullseyeSections(rows, postedSections) {
  const bases = new Map(rows.map(row => [resultKey(row), row]));
  const enriched = postedSections.map(section => ({ ...section, rows: section.rows.map(row => ({ ...bases.get(row.sourceRowKey), ...row })) }));
  return bullseyeLiveSections(rows, enriched).map((section, index) => ({
    ...section, label: index === 0 ? "Grand aggregate" : section.label,
    rows: section.rows.map(row => ({ ...row, cardId: row.raw.sourceRowKey || resultKey(row.raw) })),
  }));
}

export function resultStandings(rows, family, { division = "all", classification = "all", participation = "all" } = {}) {
  // Apply competition scopes before ranking; apply name searches afterwards.
  const scoped = rows.filter(row => (classification === "all" || (row.classification || "Unclassified") === classification)
    && (participation === "all" || row.participation === participation));
  return rankLiveRows(scoped, family, division);
}

export function ordinal(value) {
  if (!value) return "Not ranked";
  const tail = value % 100;
  return `${value}${tail >= 11 && tail <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" })[value % 10] || "th"}`;
}
